import {
  type APIPriorityTraversal,
  type APIViewpointAttributes,
  type ArdoqId,
  DEFAULT_OVERVIEW_PRIORITY_TRAVERSAL_ENTRY,
} from '@ardoq/api-types';
import SparkMD5 from 'spark-md5';

// The Default overview has no viewpoint row -- it is synthesised per component
// type -- but it still needs an id, because it goes through the same id-keyed
// lookups, list keys and current-viewpoint comparisons as a saved viewpoint.
//
// Ardoq ids are 24 hex characters. This prefix reserves the first 12,
// leaving the remaining 12 to distinguish component types.
const DEFAULT_OVERVIEW_ID_PREFIX = 'ffffffffffff';
const DEFAULT_OVERVIEW_ID_SUFFIX_LENGTH = 12;

// The single id used for every component type before the Default overview
// became selectable per type. Slides and shared URLs still carry it, and
// Lucidchart export breaks on an unrecognised one (ARD-31605).
const LEGACY_DEFAULT_OVERVIEW_ID = '3b958e33472939061510a0cc';

export const getDefaultOverviewId = (componentTypeName: string): ArdoqId =>
  DEFAULT_OVERVIEW_ID_PREFIX +
  SparkMD5.hash(componentTypeName).slice(0, DEFAULT_OVERVIEW_ID_SUFFIX_LENGTH);

export const isLegacyDefaultOverviewId = (id: ArdoqId): boolean =>
  id === LEGACY_DEFAULT_OVERVIEW_ID;

export const isDefaultOverviewId = (id: ArdoqId): boolean =>
  id.startsWith(DEFAULT_OVERVIEW_ID_PREFIX) || isLegacyDefaultOverviewId(id);

export const isDefaultOverview = ({
  _id,
}: Pick<APIViewpointAttributes, '_id'>): boolean => isDefaultOverviewId(_id);

// The backend stores the Default overview in a priority traversal list as a
// sentinel entry, and the rest of the app selects and orders it by its
// per-type id. These convert between the two.
export const toPriorityViewpointIdsForType = ({
  componentTypeName,
  entries,
}: APIPriorityTraversal): ArdoqId[] =>
  entries.map(entry =>
    entry === DEFAULT_OVERVIEW_PRIORITY_TRAVERSAL_ENTRY
      ? getDefaultOverviewId(componentTypeName)
      : entry
  );

// Every component type's list, flattened in order. Each id belongs to the
// component type whose list it came from.
export const toPriorityViewpointIds = (
  priorityTraversals: APIPriorityTraversal[]
): ArdoqId[] => priorityTraversals.flatMap(toPriorityViewpointIdsForType);

type ToPriorityTraversalArgs = {
  componentTypeName: string;
  viewpointIds: ArdoqId[];
};

export const toPriorityTraversal = ({
  componentTypeName,
  viewpointIds,
}: ToPriorityTraversalArgs): APIPriorityTraversal => {
  const defaultOverviewId = getDefaultOverviewId(componentTypeName);
  return {
    componentTypeName,
    entries: viewpointIds.map(id =>
      id === defaultOverviewId ? DEFAULT_OVERVIEW_PRIORITY_TRAVERSAL_ENTRY : id
    ),
  };
};

type IsCurrentViewpointIdArgs = {
  viewpointId: ArdoqId;
  currentViewpointId: ArdoqId | null;
  isWildcardTraversal: boolean;
};

// Only the legacy id is type-agnostic, so it matches whichever component type
// is in context. A per-type id has to match exactly, which the equality check
// already covers -- accepting any Default overview id here would mark one
// type's Default overview as current while another type's is loaded.
export const isCurrentViewpointId = ({
  viewpointId,
  currentViewpointId,
  isWildcardTraversal,
}: IsCurrentViewpointIdArgs): boolean => {
  if (currentViewpointId === viewpointId) return true;
  if (!isDefaultOverviewId(viewpointId)) return false;
  return currentViewpointId
    ? isLegacyDefaultOverviewId(currentViewpointId)
    : isWildcardTraversal;
};
